import React, { useEffect, useRef, useState } from 'react';
import { useNotifications } from '../context/NotificationContext';

function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function NotificationBell() {
  const {
    notifications,
    unreadCount,
    loading,
    notificationLabels,
    markAsRead,
    markAllAsRead,
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  return (
    <div ref={wrapperRef} className='relative'>
      <button
        type='button'
        onClick={() => setOpen((next) => !next)}
        className='relative h-10 w-10 rounded-full border border-gray-200 bg-white text-gray-700 hover:border-orange-300 hover:text-orange-600 transition-colors flex items-center justify-center'
        aria-label='Notifications'
      >
        <svg width='20' height='20' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
          <path d='M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
          <path d='M13.73 21a2 2 0 0 1-3.46 0' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
        {unreadCount > 0 && (
          <span className='absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center'>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className='absolute right-0 mt-3 w-80 sm:w-96 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden'>
          <div className='flex items-center justify-between px-4 py-3 border-b border-gray-100'>
            <div>
              <h3 className='font-bold text-gray-900'>Notifications</h3>
              <p className='text-xs text-gray-500'>{unreadCount} unread</p>
            </div>
            {unreadCount > 0 && (
              <button
                type='button'
                onClick={markAllAsRead}
                className='text-sm font-semibold text-orange-600 hover:text-orange-700'
              >
                Mark all read
              </button>
            )}
          </div>

          <div className='max-h-96 overflow-y-auto'>
            {loading && notifications.length === 0 ? (
              <div className='px-4 py-8 text-center text-sm text-gray-500'>Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className='px-4 py-8 text-center text-sm text-gray-500'>No notifications yet</div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  type='button'
                  onClick={() => markAsRead(notification.id)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-orange-50 transition-colors ${
                    notification.read ? 'bg-white' : 'bg-orange-50/60'
                  }`}
                >
                  <div className='flex items-start gap-3'>
                    <span className={`mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0 ${
                      notification.read ? 'bg-gray-300' : 'bg-orange-500'
                    }`} />
                    <span className='min-w-0 flex-1'>
                      <span className='block text-sm font-semibold text-gray-900'>
                        {notificationLabels[notification.type] || notificationLabels.info}
                      </span>
                      <span className='block text-sm text-gray-700 break-words'>
                        {notification.message}
                      </span>
                      <span className='block text-xs text-gray-500 mt-1'>
                        {formatTime(notification.createdAt)}
                      </span>
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
